import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { checkEstimationExistsRequestError, setEstimationReady } from '../../../actions/checkEstimationExists';
import { getEstimateWithParameters } from '../../../actions/estimation';
import { setPageType, setQueryParams, updateUrl } from '../../../actions/page';
import { getPreciseFilters, setPreciseFiltersState } from '../../../actions/preciseFilters';
import { checkExistRateOurEstimation } from '../../../actions/rateOurEstimation';
import { selectPageType } from '../../../selectors/page';
import { selectIsAgent } from '../../../selectors/user';
import { TThunkDispatch } from '../../../types/redux';
import { EPageType } from '../../../types/redux/page';
import { useScrollToBlock } from '../../Scrolling';
import { useTrackCalculateClick } from '../tracking';

/**
 * Вызываем апи оценки на текущей странице с новыми фильтрами
 *  - либо агент выбрал другие параметры оценки (адрес/комнатность/площадь)
 *  - либо поикун нажал "перейти к оценке" в плашке "Кажется вы уже оценивали этот объекта"
 */
export function useHandleValuationSubmit() {
  const dispatch = useDispatch<TThunkDispatch>();
  const scrollToBlock = useScrollToBlock();
  const trackCalculateClick = useTrackCalculateClick();
  const isAgent = useSelector(selectIsAgent);
  const pageType = useSelector(selectPageType);
  const isOldPageTypeSuburban = pageType === EPageType.CalculatorSuburban;

  return React.useCallback(async () => {
    /**
     * При ручном изменении основных фильтров и нажатии на "Оценить":
     * сбрасываем offerId и estimationId, т.к это уже не оценка по офферу в чистом виде
     *
     * Так же если была смена категории оценки недвижимости (из загородки в квартиру)
     *  - сбрасываем все уточняющие фильтры, выбранные в загородке и перезапрашиваем
     *    набор уточняющих фильтров для квартир
     *  - сбрасываем lat/lng из урла (нужны только для загородки)
     * */
    dispatch(setPageType(isAgent ? EPageType.ReportCreator : EPageType.Calculator));
    dispatch(setQueryParams({ offerId: undefined, estimationId: undefined, lat: undefined, lng: undefined }));
    if (isOldPageTypeSuburban) {
      dispatch(setPreciseFiltersState({}));
    }
    dispatch(updateUrl());

    const [estimationResult] = await Promise.all([
      dispatch(getEstimateWithParameters({ validateFilters: true }, { scrollToBlock })),
      dispatch(checkExistRateOurEstimation()),
      isOldPageTypeSuburban ? dispatch(getPreciseFilters()) : Promise.resolve(),
    ]);

    /**
     * Сбрасывает id оценки или offer из проверки оценки для пользователя (расплата)
     * чтобы скрыть плашку "Кажется вы уже оценивали этот адрес"
     * кейс когда сменили основные фильтры и нажали "перейти к оценке"
     */
    dispatch(
      setEstimationReady({
        estimationId: null,
        offer: null,
      }),
    );

    if (!(estimationResult instanceof Error) && !estimationResult.data) {
      dispatch(checkEstimationExistsRequestError());
    }

    trackCalculateClick();
  }, [dispatch, isAgent, isOldPageTypeSuburban, scrollToBlock, trackCalculateClick]);
}
